import { FC, useCallback, useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AtendimentoEstado, AtendimentoType, showError } from '../../../../../_metronic/helpers'
import { ListLoading, useToast } from '../../../../components'
import { InfoAlert } from '../../../../components/Info/InfoAlert'
import { useAsideRecolhido } from '../../../../helpers/hooks/useAsideRecolhido'
import { useAuth } from '../../../auth'
import { temVinculoMedicoAtivoNaEmpresa } from 'app/modules/auth/core/vinculos'
import { getAtendimentoById } from '../../atendimento/core/_requests'
import { FormularioAtendimentoList } from '../../avaliacoes/avaliacoes-pendentes-list/core/_models'
import { ReplicasSolicitante } from '../atendimentos-medico-list/component/AtendimentoInfo/ReplicasSolicitante'
import { updateAtendimento } from '../atendimentos-medico-list/core/_requests'
import { etapaDoParecer } from './core/etapaParecer'
import { LISTA_ATENDIMENTOS } from './core/rotas'
import { useAssinarParecer } from './core/useAssinarParecer'
import { EtapaParecer } from './components/EtapaParecer'
import { ExamesSolicitacao } from './components/ExamesSolicitacao'
import { ParecerEditor } from './components/ParecerEditor'
import { SolicitacaoResumo } from './components/SolicitacaoResumo'
import styles from './ParecerInterconsulta.module.css'

/**
 * Parecer da interconsulta pelo teleconsultor, numa tela própria (no lugar da sequência de modais
 * da lista): a solicitação, os exames e as réplicas ficam à vista enquanto ele responde, e o
 * diagnóstico é parte do formulário. Só front: usa as mesmas rotas que a lista já usava.
 *
 * A página carrega o atendimento e monta o layout; o que aparece à direita vem de `etapaDoParecer`.
 */
export const ParecerInterconsultaPage: FC = () => {
  const { atendimentoId: param } = useParams()
  const atendimentoId = Number(param)
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const [assumindo, setAssumindo] = useState(false)
  const [assinando, setAssinando] = useState(false)
  const assinarParecer = useAssinarParecer()
  const { showToast } = useToast()
  const [escolhido, setEscolhido] = useState<FormularioAtendimentoList>()
  useAsideRecolhido()

  const {
    data: atendimento,
    isLoading,
    refetch,
  } = useQuery(['parecer-interconsulta', atendimentoId], () => getAtendimentoById(atendimentoId), {
    enabled: atendimentoId > 0,
    retry: false,
    refetchOnWindowFocus: false,
    onError: showError,
  })
  const recarregar = useCallback(() => refetch(), [refetch])

  // Outros tipos de atendimento têm a própria tela (teleatendimento).
  useEffect(() => {
    if (atendimento && atendimento.tipo !== AtendimentoType.Interconsulta) {
      navigate(LISTA_ATENDIMENTOS, { replace: true })
    }
  }, [atendimento, navigate])

  if (isLoading) return <ListLoading />
  if (!atendimento) {
    return (
      <InfoAlert variant='danger' role='alert'>
        Não foi possível abrir esta interconsulta. <Link to={LISTA_ATENDIMENTOS}>Voltar</Link>
      </InfoAlert>
    )
  }

  const pessoaId = Number(currentUser?.pessoa?.id)
  const etapa = etapaDoParecer({
    atendimento,
    pessoaId,
    podeResponder: temVinculoMedicoAtivoNaEmpresa(
      currentUser?.vinculos,
      atendimento.paciente?.empresa?.id
    ),
    escolhido,
  })

  const assumir = async () => {
    setAssumindo(true)
    try {
      await updateAtendimento(atendimentoId, {
        estado: AtendimentoEstado.EmAtendimento,
        responsavel: pessoaId,
        salaVideo: false,
      })
      await recarregar()
    } catch (error) {
      showError(error)
    } finally {
      setAssumindo(false)
    }
  }

  // Com o atendimento já finalizado, a assinatura conclui a interconsulta: volta para a lista, como
  // depois de finalizar.
  const assinar = async () => {
    setAssinando(true)
    try {
      if (!(await assinarParecer(atendimentoId))) return
      showToast({ variant: 'success', title: 'Parecer assinado. O solicitante foi avisado.' })
      navigate(LISTA_ATENDIMENTOS)
    } finally {
      setAssinando(false)
    }
  }

  return (
    <div className={styles.layout}>
      <aside className={styles.solicitacao} aria-label='Solicitação da interconsulta'>
        <SolicitacaoResumo atendimento={atendimento} onAtualizar={recarregar} />
        <ExamesSolicitacao atendimento={atendimento} />
      </aside>
      <div className={styles.parecer}>
        <ReplicasSolicitante atendimento={atendimento} />
        {etapa.tipo === 'responder' ? (
          <ParecerEditor
            key={etapa.formulario.id}
            atendimento={atendimento}
            formulario={etapa.formulario}
            onAtualizar={recarregar}
          />
        ) : (
          <EtapaParecer
            etapa={etapa}
            atendimento={atendimento}
            onAssumir={assumir}
            assumindo={assumindo}
            onAssinar={assinar}
            assinando={assinando}
            onEscolherFormulario={setEscolhido}
          />
        )}
      </div>
    </div>
  )
}
